import { useState } from 'react';
import Lightbox from './Lightbox';
import { FaTachometerAlt, FaGasPump, FaCogs } from 'react-icons/fa';

export default function CarCard({ car }) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const fallbackImage = 'https://picsum.photos/1200/400?random=1';

  const getImageUrl = (image) => {
    if (!image) return fallbackImage;
    return image.startsWith('http') ? image : `${process.env.REACT_APP_API_URL}${image}`;
  };

  const formatValue = (value) => {
    return value != null ? value.toLocaleString() : 'N/A';
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-150 hover:shadow-xl hover:scale-[1.01] transition-all duration-300 max-w-sm mx-auto sm:max-w-xs flex flex-col group overflow-hidden h-full">
      
      {/* Image Area with Zoom Effects */}
      <div className="relative overflow-hidden aspect-[4/3] flex-shrink-0">
        <img
          src={car.images && car.images.length > 0 ? getImageUrl(car.images[0]) : fallbackImage}
          alt={`${car.make} ${car.model}`}
          className="w-full h-full object-cover cursor-pointer transition-transform duration-500 group-hover:scale-105"
          onClick={() => setIsLightboxOpen(true)}
          onError={(e) => {
            e.target.src = fallbackImage;
            e.target.alt = 'Image not available';
          }}
        />
        {/* Dynamic Condition Tag */}
        {car.condition && (
          <span className="absolute top-3 left-3 bg-red-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-sm select-none">
            {car.condition}
          </span>
        )}
      </div>

      {/* Description Content - Changed to Standard 'p-5 sm:p-6' padding to prevent edge crowding */}
      <div className="p-5 sm:p-6 flex flex-col justify-between flex-grow">
        <div className="space-y-4">
          <div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-blue-900 transition-colors line-clamp-1">
              {`${car.make} ${car.model} - ${car.year}`}
            </h3>
            <p className="text-xs text-gray-500 line-clamp-1 mt-1 font-light">
              {car.other_info || 'No further specifications provided.'}
            </p>
          </div>

          {/* Icon Attributes Section - Clean y-borders separated cleanly from the edge */}
          <div className="grid grid-cols-3 gap-2 border-y border-gray-100 py-3 text-center">
            <div className="flex flex-col items-center">
              <FaTachometerAlt className="w-4 h-4 mb-1 text-blue-900" />
              <span className="text-[10px] font-medium text-gray-700 leading-tight">
                {formatValue(car.mileage)} Mi
              </span>
            </div>
            <div className="flex flex-col items-center border-x border-gray-100">
              <FaGasPump className="w-4 h-4 mb-1 text-blue-900" />
              <span className="text-[10px] font-medium text-gray-700 leading-tight">
                {car.fuel_type || 'N/A'}
              </span>
            </div>
            <div className="flex flex-col items-center">
              <FaCogs className="w-4 h-4 mb-1 text-blue-900" />
              <span className="text-[10px] font-medium text-gray-700 leading-tight">
                {car.transmission || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Pricing Area - Placed cleanly at the card base */}
        <div className="flex justify-between items-center mt-5 pt-1">
          <span className="text-lg font-extrabold text-gray-900 tracking-tight">
            ₦{parseFloat(car.price || 0).toLocaleString()}
          </span>
          <button
            onClick={() => setIsLightboxOpen(true)}
            className="text-xs font-bold text-red-600 hover:text-red-700 hover:underline transition-colors flex items-center space-x-1"
          >
            <span>View Details</span>
            <span>→</span>
          </button>
        </div>
      </div>

      {isLightboxOpen && (
        <Lightbox car={car} onClose={() => setIsLightboxOpen(false)} />
      )}
    </div>
  );
}
